/**
 * Proration Management Component (Phase 2)
 * Admin interface for reviewing and applying mid-term student-count growth charges
 */

import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Alert, Modal, Spinner
} from 'react-bootstrap';
import { getProrationOverview, applyProration, getProrationHistory } from '../../services/billingPhase2Service';
import { TrendUp, Lightning, ClockCounterClockwise } from '@phosphor-icons/react';
import './LateFeeManagement.css';

export const ProrationManagement = () => {
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historySub, setHistorySub] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    setLoading(true);
    setError(null);

    try {
      const overview = await getProrationOverview();
      setSubscriptions(overview);
    } catch (err) {
      setError(err.error || 'Failed to load proration overview');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyProration = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await applyProration();
      setSuccess(
        `Applied proration to ${result.subscriptions_prorated} subscription(s), ` +
        `GHS ${result.total_charged.toFixed(2)} charged`
      );
      loadOverview();
    } catch (err) {
      setError(err.error || 'Failed to apply proration');
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async (sub) => {
    setHistorySub(sub);
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const data = await getProrationHistory(sub.subscription_id);
      setHistory(data);
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const pendingCount = subscriptions.filter(s => s.pending_delta > 0).length;
  const totalPendingPreview = subscriptions.reduce((sum, s) => sum + (s.pending_charge_preview || 0), 0);
  const totalAlreadyCharged = subscriptions.reduce((sum, s) => sum + (s.already_charged || 0), 0);

  return (
    <Container fluid className="late-fee-management mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="section-title"><TrendUp size={20} className="me-2" /> Proration Management</h2>
          <p className="text-muted">
            Review and charge for mid-term student-count growth. Shrinkage issues no credit —
            only net growth beyond what's already been billed is charged, pro-rated for the days
            remaining in the billing period.
          </p>
        </Col>
        <Col md="auto">
          <Button
            variant="primary"
            onClick={handleApplyProration}
            disabled={loading || pendingCount === 0}
            size="lg"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <Lightning size={18} className="me-2" />
                Apply Proration
              </>
            )}
          </Button>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Subscriptions with Growth</h6>
              <h3 className="text-primary mb-0">{pendingCount}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Pending Charge (Preview)</h6>
              <h3 className="mb-0">GHS {totalPendingPreview.toFixed(2)}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Already Charged</h6>
              <h3 className="mb-0">GHS {totalAlreadyCharged.toFixed(2)}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Active Subscriptions</h6>
              <h3 className="mb-0">{subscriptions.length}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Subscriptions Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Billed vs Current Student Counts</h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : subscriptions.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>School ID</th>
                        <th className="text-center">Billed</th>
                        <th className="text-center">Current</th>
                        <th className="text-center">Pending Growth</th>
                        <th className="text-end">Pending Charge (Preview)</th>
                        <th className="text-end">Already Charged</th>
                        <th className="text-center">Days Remaining</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.subscription_id}>
                          <td>
                            <small className="text-muted">{sub.school_id}</small>
                          </td>
                          <td className="text-center">{sub.reconciled_student_count}</td>
                          <td className="text-center">{sub.current_student_count}</td>
                          <td className="text-center">
                            {sub.pending_delta > 0 ? (
                              <Badge bg="warning">+{sub.pending_delta}</Badge>
                            ) : (
                              <Badge bg="secondary">0</Badge>
                            )}
                          </td>
                          <td className="text-end">
                            {sub.pending_charge_preview > 0 ? (
                              <strong className="text-primary">GHS {sub.pending_charge_preview.toFixed(2)}</strong>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-end">GHS {sub.already_charged.toFixed(2)}</td>
                          <td className="text-center">{sub.remaining_days}</td>
                          <td className="text-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleViewHistory(sub)}
                            >
                              <ClockCounterClockwise size={16} className="me-1" />
                              History
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="alert alert-info" role="alert">
                  <strong>No active subscriptions in a current billing period.</strong>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Proration History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historySub && (
            <p className="text-muted mb-3">
              School: <strong>{historySub.school_id}</strong>
            </p>
          )}
          {historyLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : history.length > 0 ? (
            <Table size="sm" hover>
              <thead>
                <tr>
                  <th>Applied</th>
                  <th className="text-center">Count Change</th>
                  <th className="text-center">Days Remaining</th>
                  <th className="text-end">Amount Charged</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td><small>{new Date(h.applied_date).toLocaleString()}</small></td>
                    <td className="text-center">{h.previous_student_count} → {h.new_student_count} (+{h.student_delta})</td>
                    <td className="text-center">{h.remaining_days}/{h.period_total_days}</td>
                    <td className="text-end">GHS {h.prorated_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="alert alert-info" role="alert">No proration charges recorded yet.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProrationManagement;
